import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { Prompt, useHistory, useParams, useLocation } from "react-router-dom"
import queryString from "query-string"

import styles from "./ChoosePasswordContainer.module.scss"
import ChoosePasswordForm from "../../forms/ChoosePasswordForm/ChoosePasswordForm"
import Popup from "../../components/Popup/Popup"
import Button from "../../components/Button/Button"
import { paths } from "../../routes/paths"
import services from "../../services"
import { selectFormattedUser } from "../../reducers/selectors"
import { userLogin } from "../../actions/user/userActions"
import { save } from "../../helpers/localStorage"
import ResetPasswordError from "../../components/ResetPasswordError/ResetPasswordError"
import Loading from "../../components/Loading/Loading"
import XYCenterAlignedWindow from "../../components/XYCenterAlignedWindow"

const wordingTexts = {
  invite_employee: {
    title: "Choose password",
    description: "Please choose your password to sign in.",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    confirmPasswordLabel: "Password confirmation",
    confirmPasswordPlaceholder: "Confirm your password",
    submitBtn: "Sign in",
    cancelPopupMsg: "Are you sure you want to cancel registration?",
    linkExpiredMsg:
      "This link has expired. Please contact the company profile administrator.",
    redirectOnCancel: paths.home,
  },
  reset_password: {
    title: "Password retrieval",
    passwordLabel: "New password",
    passwordPlaceholder: "Enter new password",
    confirmPasswordLabel: "Confirm password",
    confirmPasswordPlaceholder: "Confirm password",
    submitBtn: "Change Password",
    cancelPopupMsg: "Are you sure you want to cancel password retrieval?",
    linkExpiredMsg:
      "This link has expired. Please send another request to reset password.",
    redirectOnCancel: paths.login,
  },
  change_password: {
    title: "Confirm Password Change",
    description: "Please confirm your password change by clicking the button below.",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password (optional)",
    confirmPasswordLabel: "Confirm password",
    confirmPasswordPlaceholder: "Confirm password (optional)",
    submitBtn: "Confirm Password Change",
    cancelPopupMsg: "Are you sure you want to cancel password change?",
    linkExpiredMsg:
      "This link has expired. Please request a new password change.",
    redirectOnCancel: paths.login,
  },
}

const ChoosePasswordContainer = () => {
  const [isBrokenToken, setIsBrokenToken] = useState(false)
  const [visiblePopup, setVisiblePopup] = useState(false)
  const [isLoading, setLoadingStatus] = useState(true)
  const [isConfirmSucceed, setConfirmStatus] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const history = useHistory()
  const { token } = useParams()
  const dispatch = useDispatch()
  const location = useLocation()

  const queryParams = queryString.parse(location.search)

  const handleCancel = () => {
    setVisiblePopup(true)
  }

  useEffect(() => {
    if (token) {
      services.employeeServices
        .checkToken(token)
        .catch((e) => {
          setIsBrokenToken(true)
        })
        .finally(() => {
          setLoadingStatus(false)
        })
    }
  }, [token])

  const handleSubmit = (password) => {
    setErrorMessage(null) // Clear previous errors
    // For password change confirmation, password is optional (backend uses stored temp password)
    const isPasswordChange = queryParams.reason === "change_password"
    return services.employeeServices
      .setPassword(isPasswordChange ? null : password, token, queryParams.reason)
      .then((res) => {
        if (!res || !res.data) {
          throw new Error("Invalid response from server")
        }

        if (queryParams.reason === "invite_employee") {
          // Check if user is an associate_company_admin (new admin)
          const userData = res.data.user || res.data
          if (!userData) {
            throw new Error("User data not found in response")
          }

          // Some backend flows (when email wasn't previously confirmed) return only a message
          // and do NOT return auth tokens / user payload. In that case, don't attempt auto-login.
          const hasUsableUserIdentity = Boolean(userData?.id || userData?.uuid || userData?.email)
          const hasAccessToken = Boolean(res.data?.access_token)
          if (!hasUsableUserIdentity || !hasAccessToken) {
            setConfirmStatus(true)
            setTimeout(() => {
              history.push(paths.login)
            }, 4000)
            return
          }

          // Normalize response for the rest of the app (many places expect { user: {...} })
          const normalizedAuthPayload = res.data.user
            ? res.data
            : { ...res.data, user: userData }
          // account_type: 5 = associate_company_admin (from enum)
          const isNewAdmin = userData.account_type === 5 || userData.account_type === 'associate_company_admin' || userData.role === 'associate_company_admin'
          
          // Log in the user first (both admin and regular employee)
          dispatch(userLogin(normalizedAuthPayload))
          save("currentUser", selectFormattedUser(normalizedAuthPayload))
          
          if (isNewAdmin) {
            // Check if account activation is required
            if (res.data.requires_activation && res.data.confirm_token) {
              // Redirect to email verification page with activation token and admin flag
              const companyName = userData.company?.name || 'your account'
              history.push(
                `${paths.emailVerifyMsgPage(res.data.confirm_token)}?admin=true&company=${encodeURIComponent(companyName)}`
              )
            } else {
              // Auto-login if already activated or no activation needed
              const companyName = userData.company?.name || 'your account'
              history.push(`${paths.adminConfirmation}?company=${encodeURIComponent(companyName)}`)
            }
          } else {
            // Regular employee - redirect to home or appropriate page
            history.push(paths.home)
          }
        }

        if (queryParams.reason === "reset_password" || queryParams.reason === "change_password") {
          setConfirmStatus(true)

          setTimeout(() => {
            history.push(paths.login)
          }, 4000)
        }
      })
      .catch((error) => {
        console.error("Password reset error:", error)
        // Extract error message from response
        const errorMsg = error?.response?.data?.error || 
                        error?.response?.data?.errors?.[0] || 
                        error?.response?.data?.message ||
                        error?.message || 
                        "Failed to reset password. Please check your password and try again."
        
        setErrorMessage(errorMsg)
      })
  }

  return (
    <div className={styles.containerWrapper}>
      <Prompt
        when={!visiblePopup && !isBrokenToken && !isConfirmSucceed}
        message={
          wordingTexts[queryParams.reason].cancelPopupMsg ||
          "Are you sure you want to cancel registration?"
        }
      />

      {isLoading ? (
        <Loading />
      ) : (
        <React.Fragment>
          {isBrokenToken && (
            <ResetPasswordError
              errorMessage={
                wordingTexts[queryParams.reason].linkExpiredMsg ||
                "This link has expired. Please contact the company profile administrator."
              }
            />
          )}

          {isConfirmSucceed && (
            <XYCenterAlignedWindow title="Congratulations! New password confirmed">
              <div className={styles.successMsgText}>
                You will be redirected to main page in few seconds
              </div>
            </XYCenterAlignedWindow>
          )}

          {!isBrokenToken && !isConfirmSucceed && (
            <div className={styles.box}>
              <h2>
                {wordingTexts[queryParams.reason].title || "Choose password"}
              </h2>

              {wordingTexts[queryParams.reason].description && (
                <p className={styles.text}>
                  {wordingTexts[queryParams.reason].description}
                </p>
              )}

              {errorMessage && (
                <div className={styles.errorMessage} style={{color: '#ff4444', marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(255, 68, 68, 0.1)', borderRadius: '4px'}}>
                  {errorMessage}
                </div>
              )}

              <ChoosePasswordForm
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                wordingTexts={wordingTexts[queryParams.reason]}
                reason={queryParams.reason}
              />
            </div>
          )}
        </React.Fragment>
      )}

      {visiblePopup && (
        <Popup
          dialog
          subtitle={
            wordingTexts[queryParams.reason].cancelPopupMsg ||
            "Are you sure you want to cancel registration?"
          }
          button={
            <Button
              buttonStyle="black"
              onClick={() => {
                history.push(
                  wordingTexts[queryParams.reason].redirectOnCancel ||
                    paths.home,
                )
              }}
            >
              Yes
            </Button>
          }
          acceptButton={
            <Button
              onClick={() => setVisiblePopup(false)}
              buttonStyle="outlinedBlack"
            >
              No
            </Button>
          }
          closeAction={() => setVisiblePopup(false)}
          isCrossIcon={true}
        />
      )}
    </div>
  )
}

export default ChoosePasswordContainer
